import { FC, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Redirect, useLocation } from 'react-router-dom';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import { StepOptions } from 'components/Stepper/types';
import * as bankRoutes from 'features/Bank/routes';
import routes from 'features/Payments/routes';
import ErrorLayout from 'layouts/ErrorLayout';

import {
  BankTransferContext,
  BankTransferCreationStep,
  BankTransferInput,
  BankTransferStepContext,
  PartialBankTransferInput,
  useBankTransferContext,
} from '../../contexts';
import Beneficiaries from '../Beneficiaries';
import AddReceipt from './steps/AddReceipt';
import ConfirmExternalDetails from './steps/ConfirmExternalDetails';
import ConfirmInternalDetails from './steps/ConfirmInternalDetails';
import ExternalTransferSuccess from './steps/ExternalTransferSuccess';
import Failure from './steps/Failure';
import InternalTransferSuccess from './steps/InternalTransferSuccess';
import { InternationalTransferAdditionalInformation } from './steps/InternationalTransferAdditionalInformation';
import { InternationalTransferAmountAndCurrencySelection } from './steps/InternationalTransferAmountAndCurrencySelection';
import { InternationalTransferConfirmation } from './steps/InternationalTransferConfirmation';
import InternationalTransferRecap from './steps/InternationalTransferRecap';
import { InternationalTransferRecipient } from './steps/InternationalTransferRecipient';
import InternationalTransferSuccess from './steps/InternationalTransferSuccess';
import PerformExternalTransfer from './steps/PerformExternalTransfer';
import PerformInternalTransfer from './steps/PerformInternalTransfer';
import { SelectFromBankAccount } from './steps/SelectFromBankAccount';
import { SelectToBankAccount } from './steps/SelectToBankAccount';
import { ExternalTransferDetails } from './steps/TransferDetails/screens/ExternalTransferDetails';
import { InternalTransferDetails } from './steps/TransferDetails/screens/InternalTransferDetails';
import { BankTransferFlowVariant } from './types';
import { newTransferNeedsReceipt } from './utils';

const includeStepIf = (
  condition: boolean,
  step: StepOptions<BankTransferCreationStep>,
) => {
  return condition ? [step] : [];
};

type CreateNewTransferLocationState =
  | {
      bankTransferRecipientId?: string;
      fromBankAccountId?: string;
      toBankAccountId?: string;
      transferType?: BankTransferInput['transferType'];
      variant?: BankTransferFlowVariant;
      amount?: number;
    }
  | undefined;

interface CreateNewTransferProps {
  variant: BankTransferFlowVariant;
  withMultipleBankAccounts: boolean;
}

const CreateNewTransfer: FC<CreateNewTransferProps> = ({
  variant,
  withMultipleBankAccounts,
}) => {
  const { bankTransfer } = useBankTransferContext('init');

  const shouldIncludeAddReceiptStep =
    bankTransfer.transferType === 'external' &&
    newTransferNeedsReceipt(bankTransfer.amount);

  const donePath =
    ['closeWallet', 'closeWalletToMainAccount'].includes(variant) &&
    bankTransfer.fromBankAccountId
      ? bankRoutes.getBankAccountPath(bankTransfer.fromBankAccountId)
      : undefined;

  const steps: StepOptions<BankTransferCreationStep>[] = useMemo(() => {
    switch (variant) {
      case 'toAccount':
        return [
          {
            key: 'selectFromAccount',
            render: ({ goBack, ...props }) => (
              <SelectFromBankAccount {...props} disableEmptyAccounts />
            ),
          },
          {
            key: 'setInternalDetails',
            render: (props) => <InternalTransferDetails {...props} />,
          },
          {
            key: 'confirmInternalDetails',
            render: (props) => <ConfirmInternalDetails {...props} />,
          },
          {
            key: 'performInternal',
            render: (props) => <PerformInternalTransfer {...props} />,
          },
          {
            key: 'internalTransferSuccess',
            render: (props) => <InternalTransferSuccess {...props} />,
          },
          {
            key: 'failure',
            render: (props) => <Failure {...props} />,
          },
        ];
      case 'toBeneficiary':
        return [
          ...includeStepIf(withMultipleBankAccounts, {
            key: 'selectFromAccount',
            render: ({ goBack, ...props }) => (
              <SelectFromBankAccount {...props} />
            ),
          }),
          {
            key: 'setExternalDetails',
            render: ({ goBack, ...props }) => (
              <ExternalTransferDetails
                goBack={withMultipleBankAccounts ? goBack : undefined}
                {...props}
              />
            ),
          },
          ...includeStepIf(shouldIncludeAddReceiptStep, {
            key: 'addReceipt',
            render: (props) => <AddReceipt {...props} />,
          }),
          {
            key: 'confirmExternalDetails',
            render: (props) => <ConfirmExternalDetails {...props} />,
          },
          {
            key: 'performExternal',
            render: (props) => <PerformExternalTransfer {...props} />,
          },
          {
            key: 'externalTransferSuccess',
            render: (props) => <ExternalTransferSuccess {...props} />,
          },
          {
            key: 'failure',
            render: (props) => <Failure {...props} />,
          },
        ];
      case 'closeWallet':
      case 'fromAccountToAccount':
        return [
          {
            key: 'selectToAccount',
            render: ({ goBack, ...props }) => (
              <SelectToBankAccount {...props} />
            ),
          },
          {
            key: 'setInternalDetails',
            render: (props) => <InternalTransferDetails {...props} />,
          },
          {
            key: 'confirmInternalDetails',
            render: (props) => <ConfirmInternalDetails {...props} />,
          },
          {
            key: 'performInternal',
            render: (props) => <PerformInternalTransfer {...props} />,
          },
          {
            key: 'internalTransferSuccess',
            render: (props) => (
              <InternalTransferSuccess {...props} donePath={donePath} />
            ),
          },
          {
            key: 'failure',
            render: (props) => <Failure {...props} />,
          },
        ];
      case 'fromAccountToBeneficiary':
        return [
          {
            key: 'selectBeneficiary',
            render: ({ goBack, ...props }) => (
              <BankTransferStepContext.Provider value={props}>
                <Beneficiaries isCreatingNewTransfer />
              </BankTransferStepContext.Provider>
            ),
          },
          {
            key: 'setExternalDetails',
            render: (props) => <ExternalTransferDetails {...props} />,
          },
          ...includeStepIf(shouldIncludeAddReceiptStep, {
            key: 'addReceipt',
            render: (props) => <AddReceipt {...props} />,
          }),
          {
            key: 'confirmExternalDetails',
            render: (props) => <ConfirmExternalDetails {...props} />,
          },
          {
            key: 'performExternal',
            render: (props) => <PerformExternalTransfer {...props} />,
          },
          {
            key: 'externalTransferSuccess',
            render: (props) => <ExternalTransferSuccess {...props} />,
          },
          {
            key: 'failure',
            render: (props) => <Failure {...props} />,
          },
        ];
      case 'closeWalletToMainAccount':
        return [
          {
            key: 'setInternalDetails',
            render: (props) => <InternalTransferDetails {...props} />,
          },
          {
            key: 'confirmInternalDetails',
            render: (props) => <ConfirmInternalDetails {...props} />,
          },
          {
            key: 'performInternal',
            render: (props) => <PerformInternalTransfer {...props} />,
          },
          {
            key: 'internalTransferSuccess',
            render: (props) => (
              <InternalTransferSuccess {...props} donePath={donePath} />
            ),
          },
          {
            key: 'failure',
            render: (props) => <Failure {...props} />,
          },
        ];
      case 'default':
      default:
        switch (bankTransfer.transferType) {
          case 'internal':
            return [
              {
                key: 'selectFromAccount',
                render: ({ goBack, ...props }) => (
                  <SelectFromBankAccount {...props} disableEmptyAccounts />
                ),
              },
              {
                key: 'selectToAccount',
                render: (props) => <SelectToBankAccount {...props} />,
              },
              {
                key: 'setInternalDetails',
                render: (props) => <InternalTransferDetails {...props} />,
              },
              {
                key: 'confirmInternalDetails',
                render: (props) => <ConfirmInternalDetails {...props} />,
              },
              {
                key: 'performInternal',
                render: (props) => <PerformInternalTransfer {...props} />,
              },
              {
                key: 'internalTransferSuccess',
                render: (props) => <InternalTransferSuccess {...props} />,
              },
              {
                key: 'failure',
                render: (props) => <Failure {...props} />,
              },
            ];
          case 'external':
            return [
              ...includeStepIf(withMultipleBankAccounts, {
                key: 'selectFromAccount',
                render: ({ goBack, ...props }) => (
                  <SelectFromBankAccount {...props} />
                ),
              }),
              {
                key: 'selectBeneficiary',
                render: ({ goBack, goNext }) => (
                  <BankTransferStepContext.Provider
                    value={{
                      goBack: withMultipleBankAccounts ? goBack : undefined,
                      goNext,
                    }}
                  >
                    <Beneficiaries isCreatingNewTransfer />
                  </BankTransferStepContext.Provider>
                ),
              },
              {
                key: 'setExternalDetails',
                render: (props) => <ExternalTransferDetails {...props} />,
              },
              ...includeStepIf(shouldIncludeAddReceiptStep, {
                key: 'addReceipt',
                render: (props) => <AddReceipt {...props} />,
              }),
              {
                key: 'confirmExternalDetails',
                render: (props) => <ConfirmExternalDetails {...props} />,
              },
              {
                key: 'performExternal',
                render: (props) => <PerformExternalTransfer {...props} />,
              },
              {
                key: 'externalTransferSuccess',
                render: (props) => <ExternalTransferSuccess {...props} />,
              },
              {
                key: 'failure',
                render: (props) => <Failure {...props} />,
              },
            ];
          case 'international':
            return [
              ...includeStepIf(withMultipleBankAccounts, {
                key: 'selectFromAccount',
                render: ({ goBack, ...props }) => (
                  <SelectFromBankAccount {...props} disableEmptyAccounts />
                ),
              }),
              {
                key: 'selectAmountAndCurrency',
                render: (props) => (
                  <InternationalTransferAmountAndCurrencySelection {...props} />
                ),
              },
              {
                key: 'internationalTransferRecipient',
                render: (props) => (
                  <InternationalTransferRecipient {...props} />
                ),
              },
              {
                key: 'internationalTransferAdditionalInformation',
                render: (props) => (
                  <InternationalTransferAdditionalInformation {...props} />
                ),
              },
              {
                key: 'addReceiptToInternationalTransfer',
                render: (props) => <AddReceipt {...props} />,
              },
              {
                key: 'internationalTransferRecap',
                render: (props) => <InternationalTransferRecap {...props} />,
              },
              {
                key: 'internationalTransferConfirmation',
                render: (props) => (
                  <InternationalTransferConfirmation {...props} />
                ),
              },
              {
                key: 'internationalTransferSuccess',
                render: (props) => <InternationalTransferSuccess {...props} />,
              },
              {
                key: 'failure',
                render: (props) => <Failure {...props} />,
              },
            ];
          default:
            throw new Error('Invalid transfer type');
        }
    }
  }, [
    withMultipleBankAccounts,
    shouldIncludeAddReceiptStep,
    variant,
    bankTransfer.transferType,
    donePath,
  ]);

  return <Stepper steps={steps} />;
};

interface CreateNewTransferContextContainerProps {
  initialState: PartialBankTransferInput;
  mainBankAccountId: string;
  variant: BankTransferFlowVariant;
  withMultipleBankAccounts: boolean;
}

const CreateNewTransferContextContainer: FC<
  CreateNewTransferContextContainerProps
> = ({
  initialState: { fromBankAccountId, transferType, ...initialState },
  mainBankAccountId,
  variant,
  withMultipleBankAccounts,
}) => {
  const [bankTransfer, setBankTransfer] = useState<PartialBankTransferInput>(
    () => ({
      fromBankAccountId:
        fromBankAccountId ??
        (withMultipleBankAccounts ? undefined : mainBankAccountId),
      transferType,
      ...initialState,
    }),
  );

  const bankTransferContextValue = useMemo(
    () => ({ bankTransfer, setBankTransfer }),
    [bankTransfer, setBankTransfer],
  );

  return (
    <ErrorBoundary
      fallback={
        <ErrorLayout>
          <ErrorCard retry={() => window.location.reload()} />
        </ErrorLayout>
      }
    >
      <BankTransferContext.Provider value={bankTransferContextValue}>
        <CreateNewTransfer
          variant={variant}
          withMultipleBankAccounts={withMultipleBankAccounts}
        />
      </BankTransferContext.Provider>
    </ErrorBoundary>
  );
};

const CreateNewTransferDataContainer: FC = () => {
  const {
    bankAccounts,
    error: bankAccountsError,
    loading: loadingBankAccounts,
    mainBankAccount,
    refetch: refetchMainBankAccountId,
  } = useCompanyBankAccounts();
  const { state: { transferType, variant = 'default', ...initialState } = {} } =
    useLocation<CreateNewTransferLocationState>();

  if (!transferType) {
    return <Redirect to={routes.transfers} />;
  }

  if (loadingBankAccounts) {
    return <Loader />;
  }

  if (bankAccountsError || !mainBankAccount) {
    return (
      <ErrorLayout>
        <ErrorCard retry={refetchMainBankAccountId} />
      </ErrorLayout>
    );
  }

  return (
    <CreateNewTransferContextContainer
      initialState={{ transferType, ...initialState }}
      mainBankAccountId={mainBankAccount.bankAccountId}
      variant={variant}
      withMultipleBankAccounts={bankAccounts.length > 1}
    />
  );
};

export default CreateNewTransferDataContainer;
